import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";

import MaterialTable from "@material-table/core";
import { ExportCsv, ExportPdf } from "@material-table/exporters";
import {
  Avatar,
  Badge,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  SwipeableDrawer,
  Tab,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import StoreIcon from "@material-ui/icons/Store";
import { Alert, TabContext, TabList, TabPanel } from "@material-ui/lab";
import {
  AddCircle,
  Print,
  Publish,
  RemoveCircle,
  TabletAndroid
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { UserContext } from "../../../UserProvider";
import { Currency } from "../../../components/misc/Currency";
import { emptyProduct, Product } from "../../../models/Product";
import { Store } from "../../../models/Store";
import {
  Authenticated,
  hasDeniedRole,
  isProductCampaignReadOnly
} from "../../../models/User";
import { isOk } from "../../../result";
import { AppState } from "../../../store";
import {
  thunkFetchProductCategories,
  thunkFetchProductsV2
} from "../../../store/products/action";
import { thunkFetchStores } from "../../../store/stores/actions";
import { getCurrencyPositiveOrNegativeColoring } from "../../statistics/shared/tableFunctions";
import { TenantProductCategories } from "./ProductCategories";
import { ProductDetails } from "./ProductDetails";
import { ProductsPrint } from "./ProductsPrint";
import { TenantProductEditor } from "./TenantProductEditor";
import useStyles from "./styles";

import Divider from "@material-ui/core/Divider";
import { Check } from "@material-ui/icons";
import MenuIcon from "@material-ui/icons/Menu";
import { useNavigate, useParams } from "react-router-dom";
import * as StoreInventoryApi from "../../../api/storeInventoryApi";
import { BrandNameMultiSelect } from "../../../components/selects/BrandNameMultiSelect";
import { CategoryMultiSelect } from "../../../components/selects/CategoryMultiSelect";
import { DistributorMultiSelect } from "../../../components/selects/DistributorMultiSelect";
import { useI18n } from "../../../hooks/useI18n";
import { ProductState } from "../../../store/products/types";
import { formatDatetime } from "../../../utils";
import { ManageProduct } from "../../stores/ProductManagement";
import { ProductToPriceTagList } from "./ProductToPriceTagList";
import { TenantProductSync } from "./TenantProductSync";

export const TenantProducts: FunctionComponent<any> = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const user = useContext(UserContext) as Authenticated;
  const theme = useTheme();
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const readOnly = isProductCampaignReadOnly(user);

  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const printingEnabled =
    user.tenant.configuration?.features?.digitalPriceTags === false;

  const [selectedTab, setSelectedTab] = useState("1");
  const [actionsOpen, setActionsOpen] = useState(false);

  const [state, setState] = useState<any>({
    editingProduct: emptyProduct(),
    reloadList: false,
    isLoading: false,
    selectedStore: null,
    showProductId: params.productId || null,
    addNewProduct: false,
    showProductEditor: false,
    productRegistrationDone: false,
    editProductDialogOpen: false,
    showDeleteSuccess: false,
    excelSyncDialogOpen: false,
    manageProductDialogOpen: false,

    showProductFilter: false,
    showPrintable: false,
    printableProducts: [],
    storeInventory: [],
    productFilters: { distributorIds: [], categoryIds: [], brandNames: [] },
    productTableColumns: {
      showEan: false,
      showArticleNumber: false,
      showPrice: true,
      showCategory: true,
      showAcquisitionPrice: false,
      showGrossMargin: false,
      showDistributor: false,
      showUpdated: false
    }
  });

  const productState = useSelector<AppState, ProductState>(
    s => s.storeClusterProducts.productState
  );

  let { products, isLoading } = productState;

  const stores = useSelector<AppState, ReadonlyArray<Store>>(
    s => s.stores.stores
  );

  if (state.productFilters) {
    const { distributorIds, categoryIds, brandNames } = state.productFilters;

    if (distributorIds.length > 0) {
      products = products.filter(p =>
        distributorIds.includes(p.orderInfo?.distributor?.id)
      );
    }

    if (categoryIds.length > 0) {
      products = products.filter(p => categoryIds.includes(p.category.id));
    }

    if (brandNames.length > 0) {
      products = products.filter(p => brandNames.includes(p.brandName));
    }
  }

  const toggleProductForPrint = (product: Product) => {
    const index = state.printableProducts.findIndex(
      (p: Product) => p.id === product.id
    );
    if (index < 0) {
      setState({
        ...state,
        printableProducts: [...state.printableProducts, product]
      });
    } else {
      let tmp = [...state.printableProducts];
      tmp.splice(index, 1);
      setState({ ...state, printableProducts: tmp });
    }
  };

  const hidePrintable = () => {
    setState({ ...state, showPrintable: false });
  };

  const isAddedForPrint = (product: Product) => {
    return !!state.printableProducts.find((p: Product) => p.id === product.id);
  };

  const handleChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setSelectedTab(newValue);
  };

  const handleActionsOpen = () => {
    setActionsOpen(true);
  };

  const handleActionsClose = () => {
    setActionsOpen(false);
  };

  useEffect(() => {
    dispatch(thunkFetchProductCategories());
    if (!products || !products.length) {
      dispatch(thunkFetchProductsV2());
    }
    if (!stores || !stores.length) {
      dispatch(thunkFetchStores());
    }
  }, []);

  useEffect(() => {
    if (state.reloadList) {
      dispatch(thunkFetchProductsV2());
      setState({ ...state, reloadList: false });
    }
  }, [state]);

  return (
    <div className={classes.root}>
      <TabContext value={selectedTab}>
        <TabList
          className={"no-print"}
          onChange={handleChange}
          style={{ borderBottom: "1px solid #e0e0e0" }}
        >
          <Tab label={t("common.tabs.products")} value="1" />
          <Tab
            label={t("common.tabs.categories")}
            value="2"
            disabled={hasDeniedRole(user, ["DistributorManager"])}
          />
          {selectedTab === "1" && !readOnly ? (
            <Box
              className={classes.tabActions}
              sx={{
                display: "flex",
                flexGrow: 1,
                justifyContent: "end",
                alignItems: "center"
              }}
            >
              <Tooltip title={t("common.tooltip.actions")}>
                <IconButton onClick={handleActionsOpen} color={"primary"}>
                  <MenuIcon />
                </IconButton>
              </Tooltip>
              <Paper>
                <SwipeableDrawer
                  anchor={"right"}
                  open={actionsOpen}
                  PaperProps={{
                    style: { position: "fixed", top: "inherit" }
                  }}
                  onClose={() => handleActionsClose()}
                  onOpen={() => handleActionsOpen()}
                >
                  <Box className={classes.drawerAction}>
                    <Tooltip title={t("common.tooltip.close")}>
                      <IconButton onClick={() => handleActionsClose()}>
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Divider variant={"fullWidth"} />
                  <Box className={classes.drawerMenuCategory}>
                    <Typography variant={"body1"} color={"textSecondary"}>
                      {t("common.messages.manageProducts")}
                    </Typography>
                  </Box>

                  <MenuItem
                    onClick={() => {
                      setState({
                        ...state,
                        addNewProduct: true,
                        showProductEditor: true
                      });
                      handleActionsClose();
                    }}
                  >
                    <Box className={classes.drawerAction}>
                      <IconButton>
                        <AddCircle
                          style={{ color: theme.palette.success.dark }}
                        />
                      </IconButton>
                      <Typography variant={"body2"} component={"div"}>
                        {t("common.messages.addNewProduct")}
                      </Typography>
                    </Box>
                  </MenuItem>

                  {!hasDeniedRole(user, ["DistributorManager"]) && (
                    <>
                      <MenuItem
                        onClick={() => {
                          setState({ ...state, manageProductDialogOpen: true });
                          handleActionsClose();
                        }}
                      >
                        <Box className={classes.drawerAction}>
                          <IconButton>
                            <TabletAndroid
                              fontSize={"medium"}
                              className={"mediumFont"}
                            />
                          </IconButton>
                          <Typography variant={"body2"} component={"div"}>
                            {t("common.messages.goToProductInStore")}
                          </Typography>
                        </Box>
                      </MenuItem>

                      <MenuItem
                        onClick={() => {
                          setState({ ...state, excelSyncDialogOpen: true });
                          handleActionsClose();
                        }}
                      >
                        <Box className={classes.drawerAction}>
                          <IconButton>
                            <Publish />
                          </IconButton>
                          <Typography variant={"body2"} component={"div"}>
                            {t("common.messages.uploadProducts")}
                          </Typography>
                        </Box>
                      </MenuItem>
                    </>
                  )}
                </SwipeableDrawer>
              </Paper>
            </Box>
          ) : (
            <span />
          )}
        </TabList>
        <TabPanel value="1" className={classes.tabPanel}>
          {!state.showPrintable && (
            <MaterialTable
              title=""
              isLoading={isLoading}
              columns={[
                {
                  title: t("common.messages.id"),
                  field: "id",
                  hidden: true,
                  searchable: true
                },
                {
                  title: t("common.messages.brand"),
                  field: "brandName",
                  hidden: true,
                  searchable: true
                },
                {
                  title: t("common.messages.product"),
                  field: "name",
                  render: (row: Product) => {
                    return (
                      <div style={{ minWidth: 180 }}>
                        <Typography>{row.name}</Typography>
                        <Typography color="textSecondary">
                          {state.productTableColumns.showEan
                            ? ""
                            : `${row.gtin}, `}
                          {row.details?.size ? `${row.details?.size}` : ""}{" "}
                          {row?.brandName}
                        </Typography>
                        {row.articleNumber &&
                          !state.productTableColumns.showArticleNumber && (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              component="p"
                            >
                              {t("common.productTable.artNbr")}{" "}
                              {row.articleNumber}
                            </Typography>
                          )}
                      </div>
                    );
                  }
                },
                {
                  title: t("common.messages.category"),
                  field: "category.name",
                  hidden: !state.productTableColumns.showCategory
                },
                {
                  title: t("common.messages.distributor"),
                  field: "orderInfo.distributor.name",
                  hidden: !state.productTableColumns.showDistributor
                },
                {
                  title: t("common.messages.ean"),
                  field: "gtin",
                  hidden: !state.productTableColumns.showEan,
                  searchable: true
                },
                {
                  title: t("common.messages.price"),
                  field: "pricing.price",
                  hidden: !state.productTableColumns.showPrice,
                  render: row => {
                    return (
                      <div>
                        <Typography>
                          <Currency
                            value={row.pricing.price}
                            priceUnit={row.pricing.priceUnit}
                          />
                          {row.pricing.depositFee?.depositAmount &&
                          row.pricing.depositFee?.depositAmount > 0 ? (
                            <Typography
                              color="textSecondary"
                              variant="body2"
                              component={"p"}
                            >
                              +
                              <Currency
                                value={row.pricing.depositFee.depositAmount}
                                priceUnit={row.pricing.priceUnit}
                              />
                            </Typography>
                          ) : (
                            <></>
                          )}
                        </Typography>
                      </div>
                    );
                  }
                },
                {
                  title: t("common.messages.articleNumber"),
                  field: "articleNumber",
                  hidden: !state.productTableColumns.showArticleNumber,
                  searchable: true
                },
                {
                  title: t("common.messages.acquisitionPrice"),
                  field: "pricing.acquisitionPrice",
                  hidden: !state.productTableColumns.showAcquisitionPrice,
                  render: row => (
                    <Currency value={row.pricing.acquisitionPrice} />
                  )
                },
                {
                  title: t("common.messages.grossMargin"),
                  field: "pricing.grossPriceMarginal",
                  hidden: !state.productTableColumns.showGrossMargin,
                  render: row =>
                    getCurrencyPositiveOrNegativeColoring(
                      row.pricing.grossPriceMarginal
                    )
                },
                {
                  title: t("common.messages.updated"),
                  field: "updated",
                  hidden: !state.productTableColumns.showUpdated,
                  searchable: true,
                  render: row => formatDatetime(row.updated)
                }
              ]}
              onRowClick={(evt, row) => {
                setState({ ...state, showProductId: row?.id });
                if (row?.id) {
                  navigate(row?.id);
                }
              }}
              data={products && products.length ? (products as []) : []}
              options={{
                pageSize: 20,
                pageSizeOptions: [50, 100, 200],
                actionsColumnIndex: -1,
                searchFieldAlignment: mobile ? "left" : "right",
                headerStyle: {
                  padding: mobile ? "8px" : ""
                },
                exportAllData: true,
                exportMenu: [
                  {
                    label: t("common.export.exportPdf"),
                    exportFunc: (cols, data) =>
                      ExportPdf(cols, data, "Products")
                  },
                  {
                    label: t("common.export.exportCsv"),
                    exportFunc: (cols, data) =>
                      ExportCsv(cols, data, "Products")
                  }
                ]
              }}
              actions={[
                {
                  hidden: !printingEnabled,
                  tooltip: t("common.actions.printLabels"),
                  isFreeAction: true,
                  icon: () => (
                    <Badge
                      badgeContent={state.printableProducts.length}
                      max={100}
                      color={"secondary"}
                    >
                      <Print />
                    </Badge>
                  ),
                  disabled: !state.printableProducts.length,
                  onClick: () => {
                    setState({ ...state, showPrintable: true });
                  }
                },
                {
                  icon: "filter_list",
                  iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
                  tooltip: t("common.actions.addFilters"),
                  isFreeAction: true,
                  onClick: () => {
                    setState({ ...state, showProductFilter: true });
                  }
                },
                rowData => ({
                  hidden: !printingEnabled,
                  tooltip: t("common.tooltip.setForPrint"),
                  icon: () => (
                    <span className={classes.togglePrintable}>
                      <Print color={"action"} />
                      {!isAddedForPrint(rowData) ? (
                        <AddCircle color={"action"} />
                      ) : (
                        <RemoveCircle className={"remove"} color={"action"} />
                      )}
                    </span>
                  ),
                  onClick: () => toggleProductForPrint(rowData)
                })
              ]}
              localization={{
                header: {
                  actions: ""
                },
                toolbar: {
                  searchPlaceholder: t("common.search"),
                  searchTooltip: t("common.searchTooltip")
                },
                pagination: {
                  labelRowsSelect: t("rows"),
                  labelDisplayedRows: t("displayedRowsTemplate"),
                  firstTooltip: t("first"),
                  previousTooltip: t("prev"),
                  nextTooltip: t("next"),
                  lastTooltip: t("last")
                },
                body: {
                  emptyDataSourceMessage: t("noRecords")
                }
              }}
            />
          )}

          {state.showPrintable && (
            <ProductsPrint
              products={state.printableProducts}
              onClose={hidePrintable}
            />
          )}
        </TabPanel>
        <TabPanel value="2" className={classes.tabPanel}>
          <TenantProductCategories />
        </TabPanel>
      </TabContext>

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"xl"}
        onClose={() => {
          setState({
            ...state,
            showProductId: null
          });
          navigate(".", { replace: true });
        }}
        open={state.showProductId != null}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography className={classes.dialogTitle}>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({
                ...state,
                showProductId: null
              });
              navigate(".", { replace: true });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <ProductDetails
            productId={state.showProductId}
            onSave={() => {
              setState({ ...state, reloadList: true });
            }}
            onDelete={() => {
              setState({
                ...state,
                showProductId: null,
                reloadList: true,
                showDeleteSuccess: true,
                storeInventory: []
              });
              navigate(".", { replace: true });
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"lg"}
        open={state.addNewProduct}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle
          disableTypography={true}
          style={{
            paddingBottom: 4,
            paddingTop: 8,
            borderBottom: `1px solid ${theme.palette.grey.A100}`,
            marginBottom: 10
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <Typography variant={"h6"}>
              {t("common.messages.addProduct")}
            </Typography>
            <IconButton
              aria-label="close"
              style={{ color: theme.palette.grey[500] }}
              onClick={() =>
                setState({
                  ...state,
                  addNewProduct: false,
                  showProductEditor: false,
                  productRegistrationDone: false,
                  addProductToStoreFailed: false,
                  addProductToStoreError: undefined,
                  editingProduct: emptyProduct()
                })
              }
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        {state.showProductEditor && (
          <DialogContent
            style={
              mobile
                ? {
                    padding: 0,
                    height: "calc(100vh - 120px)",
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column"
                  }
                : {
                    padding: 0,
                    height: "calc(100vh - 200px)",
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column"
                  }
            }
          >
            <TenantProductEditor
              adding={
                state.editingProduct.id ===
                "00000000-0000-0000-0000-000000000000"
              }
              editingProduct={state.editingProduct}
              onSave={editingProduct => {
                setState({
                  ...state,
                  editingProduct: editingProduct,
                  reloadList: true,
                  productRegistrationDone: true,
                  showProductEditor: false
                });
              }}
            />
          </DialogContent>
        )}
        {state.productRegistrationDone && (
          <DialogContent>
            <div
              style={{
                display: "flex",
                marginTop: 10,
                justifyContent: "center",
                flexDirection: "column"
              }}
            >
              {user.tenant?.configuration?.features?.digitalPriceTags ? (
                <>
                  <ProductToPriceTagList
                    stores={stores}
                    gtin={state.editingProduct.gtin}
                  />
                </>
              ) : (
                <div>
                  <Alert severity="success">
                    {t("common.messages.productRegisteredSuccessfully")}
                  </Alert>
                  <Alert style={{ marginTop: 5 }} severity="warning">
                    {t("common.messages.productRegisteredSuccessfullyWarning")}
                  </Alert>
                  <div>
                    <div
                      style={{
                        display: "flex",
                        marginTop: 10,
                        justifyContent: "center"
                      }}
                    >
                      <List>
                        {stores.map(s => {
                          const added = state.storeInventory.find(
                            (si: any) =>
                              si.storeId === s.id &&
                              si.gtin === state.editingProduct.gtin
                          );
                          return (
                            <ListItem key={s.id}>
                              <ListItemAvatar>
                                <Avatar
                                  style={{
                                    backgroundColor: added
                                      ? theme.palette.primary.main
                                      : theme.palette.grey[500]
                                  }}
                                >
                                  <StoreIcon />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                key={s.name}
                                primary={s.name}
                                style={{ marginRight: 10 }}
                              />
                              <IconButton
                                disabled={added}
                                onClick={async () => {
                                  if (added || state.isLoading) return;

                                  setState({ ...state, isLoading: true });
                                  const result = await StoreInventoryApi.add(
                                    s.id,
                                    {
                                      gtin: state.editingProduct.gtin,
                                      hasPriceTag: false
                                    }
                                  );

                                  if (isOk(result)) {
                                    setState({
                                      ...state,
                                      isLoading: false,
                                      storeInventory: [
                                        ...state.storeInventory,
                                        {
                                          storeId: s.id,
                                          gtin: state.editingProduct.gtin
                                        }
                                      ]
                                    });
                                  } else {
                                    setState({
                                      ...state,
                                      addProductToStoreError:
                                        result.details.detail,
                                      addProductToStoreFailed: true,
                                      isLoading: false
                                    });
                                  }
                                }}
                              >
                                {added ? (
                                  <div>
                                    <Check
                                      fontSize={"small"}
                                      style={{
                                        color: "rgb(31, 132, 52)"
                                      }}
                                    />
                                  </div>
                                ) : state.isLoading ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <AddCircle
                                    fontSize={"small"}
                                    style={{
                                      color: "rgb(31, 132, 52)",
                                      cursor: "pointer"
                                    }}
                                  />
                                )}
                              </IconButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </div>
                  </div>
                  {state?.addProductToStoreFailed == true && (
                    <div
                      style={{
                        display: "flex",
                        marginTop: 20,
                        justifyContent: "center"
                      }}
                    >
                      <Alert severity="error">
                        {state.addProductToStoreError}
                      </Alert>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        )}
      </Dialog>
      <Dialog
        fullWidth={true}
        maxWidth={"md"}
        open={state.manageProductDialogOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({ ...state, manageProductDialogOpen: false });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          {stores?.length === 1 ? (
            <ManageProduct
              storeId={stores[0].id}
              storeName={stores[0].name}
              showScanner={true}
              onScannerClosed={() => {
                setState({ ...state, manageProductDialogOpen: false });
              }}
            />
          ) : (
            <ManageProduct
              onScannerClosed={() => {
                setState({ ...state, manageProductDialogOpen: false });
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.showProductFilter}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({ ...state, showProductFilter: false });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <div>
            <CategoryMultiSelect
              className={classes.field}
              value={state.productFilters.categoryIds}
              onSelected={categories => {
                setState({
                  ...state,
                  productFilters: {
                    ...state.productFilters,
                    categoryIds:
                      categories.length > 0 ? categories.map(c => c.id) : []
                  }
                });
              }}
            />
            <DistributorMultiSelect
              className={classes.field}
              value={state.productFilters.distributorIds}
              onSelected={distributors => {
                setState({
                  ...state,
                  productFilters: {
                    ...state.productFilters,
                    distributorIds:
                      distributors.length > 0 ? distributors.map(d => d.id) : []
                  }
                });
              }}
            />
            <BrandNameMultiSelect
              className={classes.field}
              value={state.productFilters.brandNames}
              onSelected={brandNames => {
                setState({
                  ...state,
                  productFilters: {
                    ...state.productFilters,
                    brandNames:
                      brandNames.length > 0 ? brandNames.map(bn => bn) : []
                  }
                });
              }}
            />
          </div>
          <div>
            <div>{t("common.messages.columns")}</div>
            <FormGroup
              style={{
                display: "flex",
                flexDirection: "row",
                marginBottom: 20
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.productTableColumns.showCategory}
                    onChange={e =>
                      setState({
                        ...state,
                        productTableColumns: {
                          ...state.productTableColumns,
                          showCategory: e.target.checked
                        }
                      })
                    }
                  />
                }
                label={t("common.formFields.category")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.productTableColumns.showPrice}
                    onChange={e =>
                      setState({
                        ...state,
                        productTableColumns: {
                          ...state.productTableColumns,
                          showPrice: e.target.checked
                        }
                      })
                    }
                  />
                }
                label={t("common.formFields.price")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.productTableColumns.showDistributor}
                    onChange={e =>
                      setState({
                        ...state,
                        productTableColumns: {
                          ...state.productTableColumns,
                          showDistributor: e.target.checked
                        }
                      })
                    }
                  />
                }
                label={t("common.formFields.distributor")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.productTableColumns.showEan}
                    onChange={e =>
                      setState({
                        ...state,
                        productTableColumns: {
                          ...state.productTableColumns,
                          showEan: e.target.checked
                        }
                      })
                    }
                  />
                }
                label={t("common.formFields.gtin")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.productTableColumns.showArticleNumber}
                    onChange={e =>
                      setState({
                        ...state,
                        productTableColumns: {
                          ...state.productTableColumns,
                          showArticleNumber: e.target.checked
                        }
                      })
                    }
                  />
                }
                label={t("common.formFields.articleNumber")}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.productTableColumns.showAcquisitionPrice}
                    onChange={e =>
                      setState({
                        ...state,
                        productTableColumns: {
                          ...state.productTableColumns,
                          showAcquisitionPrice: e.target.checked
                        }
                      })
                    }
                  />
                }
                label={t("common.formFields.acquisitionPrice")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.productTableColumns.showGrossMargin}
                    onChange={e =>
                      setState({
                        ...state,
                        productTableColumns: {
                          ...state.productTableColumns,
                          showGrossMargin: e.target.checked
                        }
                      })
                    }
                  />
                }
                label={t("common.messages.grossMarginal")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.productTableColumns.showUpdated}
                    onChange={e =>
                      setState({
                        ...state,
                        productTableColumns: {
                          ...state.productTableColumns,
                          showUpdated: e.target.checked
                        }
                      })
                    }
                  />
                }
                label={t("common.formFields.updated")}
              />
            </FormGroup>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        maxWidth={"md"}
        open={state.excelSyncDialogOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({
                ...state,
                excelSyncDialogOpen: false
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent dividers>
          <TenantProductSync />
        </DialogContent>
      </Dialog>

      {/* <Dialog
        fullWidth={true}
        maxWidth={"md"}
        open={state.priceUpdateDialogOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <Typography variant="h6">{t("common.messages.updatePrices")}</Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({
                ...state,
                priceUpdateDialogOpen: false
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent dividers>
          <TenantPriceUpdate />
        </DialogContent>
      </Dialog> */}
    </div>
  );
};
